<script lang="ts" setup>
import bannerSvg from '#core/assets/img/icon/banner.svg';
import { AppBannerBackground, AppButton, AppPassword, AppTextField, AppTypography } from '#core/components';
import type { CommonErrorResponse } from '#core/types/ErrorResponse';
import type { FormErrors } from '#core/types/Form';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useDisplay } from 'vuetify/framework';
import { useAuthStore } from '../store/auth';
import type { LoginForm } from '../types';

const { validations } = useValidation();
const { getSession } = useAuth();
const { setToken } = useAuthState();
const router = useRouter();
const route = useRoute();
const display = useDisplay();

const tabletAndLessSize = computed(() => {
  return display.width.value <= 1366;
});

const formData = ref<LoginForm>({
  email: '',
  password: '',
});

const rules = computed(() => ({
  email: {
    required: validations.required('Email'),
    email: validations.email(),
  },
  password: {
    required: validations.required('Password'),
  },
}));

const { form, handleSubmit, getErrors, v$ } = useForm<LoginForm>({
  form: formData,
  rules: rules,
});

const hasSubmitted = ref(false);

const { user, isVerified, hasCompletedOnboarding } = storeToRefs(useAuthStore());

// Get the redirect URL from query params
const redirectUrl = computed(() => {
  return (route.query.callbackUrl as string) || (route.query.redirect as string) || null;
});

const {
  pending: verifyLoading,
  execute: execSendVerify,
  error: verifyError,
} = useSubmit<{}, { data: CommonErrorResponse }>();

const { token, initToken } = await useGenerateReCaptcha('login');
const { data, pending: loginLoading, execute: execSubmit, error } = useSubmit();

const loginError = ref<{ data: FormErrors<LoginForm> }>({ data: { errors: {}, message: '' } });

const showAccountBlockedDialog = ref(false);

const handleSignIn = async (): Promise<void> => {
  hasSubmitted.value = true;

  // Manually trigger validation
  v$.value.$touch();

  await handleSubmit(async () => {
    try {
      loginLoading.value = true;
      await execSubmit('auth/login', {
        method: 'POST',
        body: {
          username: form.value.email,
          password: form.value.password,
        },
        headers: {
          ...(token.value && { 'X-Recaptcha-Token': token.value }),
        },
      });

      if (error.value) {
        // Check if account is blocked
        const errorData = error.value as any;

        if (errorData?.data?.error_code === 'ACCOUNT_BLOCKED' || errorData?.data?.is_blocked) {
          showAccountBlockedDialog.value = true;
          await initToken();
          return;
        }

        loginError.value = error.value as { data: FormErrors<LoginForm> };
        await initToken();
        return;
      }

      if (data.value) {
        setToken(data.value.data.access_token);

        user.value = data.value.data.user;

        await getSession();

        // Wait for next tick to ensure session is propagated
        await nextTick();

        if (!isVerified.value) {
          await execSendVerify('auth/verification/resend', {
            method: 'POST',
            body: { via: 'email' },
          });

          if (!verifyError.value) {
            await router.push('/verify');
          }
          return;
        }

        if (!hasCompletedOnboarding.value) {
          await router.push('/onboarding/setup-account');
          return;
        }

        const targetUrl = redirectUrl.value || '/';
        await router.push(targetUrl);
      }
    } catch (error) {
      await initToken();
      loginError.value = error as { data: FormErrors<LoginForm> };
    } finally {
      loginLoading.value = false;
    }
  });
};

const errors = computed(() => {
  return getErrors(loginError.value?.data?.errors);
});

const validationErrors = computed(() => {
  // Only show errors after form has been submitted
  if (!hasSubmitted.value) {
    return { email: [], password: [] };
  }

  const emailErrors = v$.value.email?.$errors.map((e: any) => e.$message) || [];
  const passwordErrors = v$.value.password?.$errors.map((e: any) => e.$message) || [];

  return {
    email: emailErrors,
    password: passwordErrors,
  };
});

const visibleErrors = computed(() => {
  const valErrors = validationErrors.value;
  const serverErrors = errors.value;

  return {
    email: [
      ...(Array.isArray(valErrors.email) ? valErrors.email : []),
      ...(Array.isArray(serverErrors.email) ? serverErrors.email : []),
    ],
    password: [
      ...(Array.isArray(valErrors.password) ? valErrors.password : []),
      ...(Array.isArray(serverErrors.password) ? serverErrors.password : []),
    ],
  };
});

const canContinue = computed(() => {
  const hasEmail = form.value.email.trim().length > 0;
  const hasPassword = form.value.password.trim().length > 0;
  return hasEmail && hasPassword;
});

const handleBackToLogin = () => {
  showAccountBlockedDialog.value = false;
};

const handleContactSupport = () => {
  // Open support email
  window.open('mailto:support@earthworkconnect.com.au', '_blank');
};
</script>

<template>
  <div class="login-static">
    <!-- Main Content -->
    <div class="main-content">
      <!-- Banner Section (Left) -->
      <div v-if="!tabletAndLessSize" class="banner-section">
        <AppBannerBackground :src="bannerSvg" />
      </div>

      <!-- Form Section (Right) -->
      <div class="form-section">
        <div class="form-container">
          <!-- Header -->
          <div class="form-header">
            <AppTypography element="h1" type="title-4" class="form-title">Welcome Back!</AppTypography>
            <AppTypography element="p" type="body-1" class="form-subtitle"
              >Enter your email and password to sign in to your account.</AppTypography
            >
          </div>

          <!-- Form Fields -->
          <form class="form-fields" @submit.prevent="handleSignIn">
            <!-- Email Field -->
            <AppTextField
              v-model="form.email"
              type="email"
              label="Email"
              placeholder="example@address.com.au"
              show-label
              :disabled="loginLoading"
              data-testid="email"
              data-cy="email"
            />

            <!-- Password Field -->
            <AppPassword v-model="form.password" placeholder="Password" :disabled="loginLoading" data-cy="password" />

            <div class="d-flex flex-column ga-4 text-center mt-3">
              <!-- Forgot Password Link -->
              <nuxt-link to="/reset-password" class="forgot-password-link"> Forgot Password </nuxt-link>

              <!-- Continue Button -->
              <AppButton
                type="submit"
                block
                class="btn-continue"
                :loading="loginLoading || verifyLoading"
                :disabled="!canContinue || loginLoading || verifyLoading"
                data-cy="submit"
                data-testid="submit"
                @click="handleSignIn"
              >
                Continue
              </AppButton>

              <!-- Sign Up Link -->
              <AppTypography element="p" type="body-1" class="signup-link">
                Don't have an account yet?
                <nuxt-link to="/register" class="signup-link-text" data-cy="sign-up">Sign up</nuxt-link>
              </AppTypography>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Account Blocked Dialog -->
    <v-dialog v-model="showAccountBlockedDialog" max-width="500" persistent>
      <v-card class="account-blocked-card">
        <!-- Title -->
        <div class="text-center mb-3">
          <AppTypography element="h2" type="title-4" class="modal-title">
            Your account has been deactivated
          </AppTypography>
        </div>

        <!-- Description -->
        <div class="text-center mb-8">
          <AppTypography element="p" type="body-1" class="description-text">
            Your account has been deactivated. Please contact support if you believe this is a mistake.
          </AppTypography>
        </div>

        <!-- Buttons -->
        <div class="buttons-container">
          <AppButton color="primary" block class="back-btn" @click="handleBackToLogin"> ← Back to Log In </AppButton>

          <AppButton variant="outlined" block class="support-btn" @click="handleContactSupport">
            Contact Support
          </AppButton>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<style lang="scss" scoped>
.login-static {
  background-color: #f7f7f8;
  font-family: 'Noto Sans', sans-serif;
}

// Main Content
.main-content {
  display: flex;
  height: calc(100vh - 72px);
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
}

// Banner Section
.banner-section {
  flex: 1 0 25%;
  width: 45%;
  padding: 24px;
  background-color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
}

// Form Section
.form-section {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 24px 40px 40px;
  background-color: #ffffff;
}

.form-container {
  width: 100%;
  max-width: 480px;
}

.form-header {
  text-align: center;
  margin-bottom: 32px;
}

.form-title {
  font-family: 'Big Shoulders Display', sans-serif;
  font-size: 28px;
  font-weight: 700;
  line-height: 32px;
  color: #01011d;
  margin: 0 0 4px 0;
}

.form-subtitle {
  font-size: 16px;
  line-height: 20px;
  color: #01011d;
  margin: 0;
}

.form-fields {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.forgot-password-link {
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
  color: #01011d;
  text-decoration: none;
  text-align: center;
  margin-top: 4px;

  &:hover {
    text-decoration: underline;
  }
}

.btn-continue {
  height: 48px !important;
  border-radius: 64px !important;
  font-size: 16px !important;
  font-weight: 700 !important;
  margin-top: 8px;
  background-color: #ffdf27 !important;
  color: #01011d !important;
  touch-action: manipulation !important;
  cursor: pointer !important;
  -webkit-tap-highlight-color: transparent;
}

.signup-link {
  text-align: center;
  font-size: 16px;
  line-height: 20px;
  color: #01011d;
  margin: 8px 0;

  .signup-link-text {
    font-weight: 700;
    color: #01011d;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

@media (max-width: 1440px) {
  .banner-section {
    flex: 0 0 45%;
    width: 45%;
  }
}

@media (max-width: 960px) {
  .main-content {
    flex-direction: column;
  }

  .banner-section {
    display: none;
  }

  .form-section {
    padding: 24px 16px;
    background-color: #ffffff;
  }
}

// Account Blocked Dialog Styles
.account-blocked-card {
  padding: 40px;
  border-radius: 40px;
  position: relative;
}

.modal-title {
  font-family: 'Big Shoulders Display', sans-serif !important;
  font-weight: 700;
}

.description-text {
  color: #414156;
}

.buttons-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.support-btn {
  height: 48px;
  border-radius: 64px;
  font-size: 16px;
  font-weight: 700;
  text-transform: none;
  border: 1px solid #01011d;
  color: #01011d;
}

.back-btn {
  height: 48px;
  border-radius: 64px;
  font-size: 16px;
  font-weight: 700;
  text-transform: none;

  :deep(.v-btn__content) {
    color: #01011d;
  }
}
</style>
